import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  useErrorToast,
  useSuccessToast,
} from '../../../../../../../components/Toast';
import PLAID_VERIFICATION_STATUS from '../../../../../../../constants/plaid_verifications/plaid_verification_status';
import ConfirmManualPlaidMicrodepositFlow from '../../../../../components/ConfirmManualPlaidMicrodepositFlow';
import useAddRenterPaymentMethod from '../../useAddRenterPaymentMethod';
import AddCardDetails from '../AddCardDetails';
import AddPlaidAccount from '../AddPlaidAccount';
import ChoosePaymentOption from '../ChoosePaymentOption';

const PaymentMethodOptions = ({
  title,
  onAdded,
  onPlaidFlowLoaded,
  onSelection,
  preferredFor,
  onBankAccountFailed,
  onPlaidFlowClosed,
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const { addRenterPaymentMethod, createManualPlaidVerification } =
    useAddRenterPaymentMethod();
  const showErrorToast = useErrorToast();
  const showSuccessToast = useSuccessToast();

  const isCardOptionSelected = ['credit', 'debit'].includes(selectedOption);
  const isBankSelected = selectedOption === 'bank-account';
  const isManualBankSelected = selectedOption === 'manually-add-bank-account';
  const isManualBankConfirmed =
    selectedOption === 'manually-add-bank-account-confirmed';

  const onCancelOption = useCallback(() => {
    setSelectedOption(null);
  }, []);

  const onConfirmManualFlow = useCallback(() => {
    setSelectedOption('manually-add-bank-account-confirmed');
  }, []);

  const onManualBankOption = useCallback(() => {
    setSelectedOption('manually-add-bank-account');
  }, []);

  const onCardAdded = useCallback(
    async (token, data) => {
      try {
        const paymentMethod = await addRenterPaymentMethod({
          token: token,
          zipCode: data.zipCode,
          cardHolder: data.cardHolder,
          preferredFor,
        });

        onAdded(paymentMethod);
        setSelectedOption(paymentMethod.id);
        showSuccessToast('Card payment method added!');
      } catch {
        showErrorToast(
          'There was a problem when trying to add card payment method.',
        );
      }
    },
    [onAdded],
  );

  const onBankAccountAdded = useCallback(
    async (_token, data) => {
      try {
        onPlaidFlowClosed?.();
        const paymentMethod = await addRenterPaymentMethod({
          plaidPublicToken: data.public_token,
          plaidAccountId: data.account_id,
          preferredFor,
        });

        onAdded(paymentMethod);
        setSelectedOption(paymentMethod.id);

        showSuccessToast('Payment method added!');
      } catch {
        showErrorToast(
          'There was a problem when trying to add payment method.',
        );
        if (onBankAccountFailed) {
          onBankAccountFailed();
        }
      }
    },
    [onAdded, preferredFor],
  );

  const onManualBankAccountAdded = useCallback(
    async (_token, data) => {
      try {
        onPlaidFlowClosed?.();
        const verificationStatus = data?.account?.verification_status;
        if (
          verificationStatus !==
          PLAID_VERIFICATION_STATUS.PENDING_MANUAL_VERIFICATION
        ) {
          onBankAccountAdded(_token, data);
          return;
        }

        const account_name = data.account.name;
        const last4 = data.account.mask;
        const plaid_account_id = data.account_id;
        const plaid_public_token = data.public_token;

        const paymentMethod = await createManualPlaidVerification({
          account_name,
          last4,
          plaid_account_id,
          plaid_public_token,
          preferred_for: preferredFor,
        });

        onAdded(paymentMethod, { pendingPlaidVerification: true });
        showSuccessToast('Payment method added!');
      } catch {
        showErrorToast(
          'There was a problem when trying to add payment method.',
        );
        if (onBankAccountFailed) {
          onBankAccountFailed();
        }
      }
    },
    [onAdded, onBankAccountAdded, preferredFor, onBankAccountFailed],
  );

  useEffect(() => {
    if (onSelection) {
      onSelection(selectedOption);
    }
  }, [selectedOption]);

  return (
    <>
      {/* all options */}
      {selectedOption === null && (
        <ChoosePaymentOption
          title={title}
          onSelection={setSelectedOption}
          onManualBankOption={onManualBankOption}
        />
      )}

      {/* credit/debit card form */}
      {isCardOptionSelected && (
        <AddCardDetails
          onCardAdded={onCardAdded}
          onBack={onCancelOption}
          cardType={selectedOption}
        />
      )}

      {/* bank account form */}
      {isBankSelected && (
        <AddPlaidAccount
          onBankAccountAdded={onBankAccountAdded}
          onLoaded={onPlaidFlowLoaded}
          onBack={onCancelOption}
        />
      )}

      {/* manual bank account - confirmation modal & form */}
      {isManualBankSelected && (
        <ConfirmManualPlaidMicrodepositFlow
          modalId="manual-plaid-flow"
          open
          onCancel={onCancelOption}
          onConfirm={onConfirmManualFlow}
        />
      )}
      {isManualBankConfirmed && (
        <AddPlaidAccount
          onLoaded={onPlaidFlowLoaded}
          onBankAccountAdded={onManualBankAccountAdded}
          offerManualMicroDeposits
          onBack={onCancelOption}
        />
      )}
    </>
  );
};

PaymentMethodOptions.propTypes = {
  title: PropTypes.string,
  onSelection: PropTypes.func,
  onPlaidFlowLoaded: PropTypes.func,
  onPlaidFlowClosed: PropTypes.func,
  onBankAccountFailed: PropTypes.func,
  onAdded: PropTypes.func.isRequired,
  preferredFor: PropTypes.oneOf([
    'auto_pay',
    'rent_payments',
    'rent_reporting',
  ]),
};
export default PaymentMethodOptions;
